/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import type { CellClickedEvent } from 'ag-grid-community'
import { App } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React, { useMemo } from 'react'
import DataGrid from 'src/components/ts-components/DataGrid'
import type {
  AttendancePlayer,
  AttendanceSectionConfig,
  AttendanceSortApi,
} from '../types'
import { buildAvailabilityOrder } from '@sportninja/common/features/game/attendance/utils/availabilityOrder'
import { isWaiverLocked } from '@sportninja/common/features/game/attendance/utils/waiverLock'
import {
  ATTENDANCE_CELL_CLASS,
  ATTENDANCE_CENTER_CLASS,
  ROW_PRESS_COLUMN_IDS,
  useAttendanceColumns,
} from '../hooks/useAttendanceColumns'
import { orderAttendanceRows } from '../utils/orderAttendanceRows'
import { getStartingHeaderTooltip } from '../utils/statusCopy'
import { AttendanceSectionContext } from './cells/AttendanceSectionContext'

export interface AttendanceSectionProps {
  label: string
  /** Optional one-line explanatory note rendered under the section title. */
  note?: string
  count: number
  list: AttendancePlayer[]
  sort: AttendanceSortApi
  waiverEnforced: boolean
  requireUniqueJerseyNumbers: boolean
  duplicateJerseyNumbers: number[]
  isGenericSport: boolean
  isSoccer: boolean
  allPlayers: AttendancePlayer[]
  isReadOnly?: boolean
  ownPlayerIds?: readonly string[]
  hideConfirm?: boolean
  /** Keeps the hidden confirm column's space so columns align with Roster Spares. */
  reserveConfirmColumn?: boolean
  /** Roster Spares: availability reads Available instead of Attending. */
  isRosterSpares?: boolean
  isSpareCandidate?: boolean
  availYesNoOnly?: boolean
  spareCandidatesLast?: boolean
  promotedRequestById?: Record<string, string>
  onPressAttendanceStatus: AttendanceSectionConfig['onPressAttendanceStatus']
  onPressRow: (player: AttendancePlayer) => void
  onUpdatePlayerNumber: AttendanceSectionConfig['onUpdatePlayerNumber']
  onStartingChange: AttendanceSectionConfig['onStartingChange']
  onPromoteSpare?: AttendanceSectionConfig['onPromoteSpare']
  onDemoteSpare?: AttendanceSectionConfig['onDemoteSpare']
  onWithdrawSpare?: AttendanceSectionConfig['onWithdrawSpare']
  onConfirmRosterSpare?: AttendanceSectionConfig['onConfirmRosterSpare']
}

const ROW_HEIGHT = 48
const HEADER_HEIGHT = 40
const LOCKED_ROW_CLASS = 'attendance-row-locked'

const AttendanceSection: React.FC<AttendanceSectionProps> = ({
  label,
  note,
  count,
  list,
  sort,
  waiverEnforced,
  requireUniqueJerseyNumbers,
  duplicateJerseyNumbers,
  isGenericSport,
  isSoccer,
  allPlayers,
  isReadOnly = false,
  ownPlayerIds = [],
  hideConfirm = false,
  reserveConfirmColumn = false,
  isRosterSpares = false,
  isSpareCandidate = false,
  availYesNoOnly = false,
  spareCandidatesLast = false,
  promotedRequestById,
  onPressAttendanceStatus,
  onPressRow,
  onUpdatePlayerNumber,
  onStartingChange,
  onPromoteSpare,
  onDemoteSpare,
  onWithdrawSpare,
  onConfirmRosterSpare,
}) => {
  const { message } = App.useApp()

  // Snapshot the availability order. Keyed on the section's player ids and the
  // re-sort counter — NOT on attendance_status — so setting a status leaves the
  // row where it is until the next explicit re-sort.
  const listKey = list.map((p) => p.id).join(',')
  const availabilityOrder = useMemo(
    () => buildAvailabilityOrder(list),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listKey, sort.availabilityResortKey]
  )

  const showStartingColumn = !isGenericSport && !isSpareCandidate
  const columnDefs = useAttendanceColumns({
    isSpareCandidate,
    hideConfirm,
    reserveConfirmColumn,
    isRosterSpares,
    showStartingColumn,
    startingLabel: isSoccer ? 'Starting Keeper' : 'Starting Goalie',
    startingTooltip: getStartingHeaderTooltip(isSoccer),
  })

  if (!list.length) {
    return null
  }

  const rows = orderAttendanceRows({
    list,
    sortType: sort.sortType,
    sortOrder: sort.sortOrder,
    hasUserSorted: sort.hasUserSorted,
    availabilityOrder,
    isSpareCandidate,
    spareCandidatesLast,
    promotedRequestById,
  })

  const config: AttendanceSectionConfig = {
    sort,
    waiverEnforced,
    requireUniqueJerseyNumbers,
    duplicateJerseyNumbers,
    isSoccer,
    allPlayers,
    isReadOnly,
    ownPlayerIds,
    isSpareCandidate,
    availYesNoOnly,
    isRosterSpares,
    promotedRequestById,
    onPressAttendanceStatus,
    onUpdatePlayerNumber,
    onStartingChange,
    onPromoteSpare,
    onDemoteSpare,
    onWithdrawSpare,
    onConfirmRosterSpare,
  }

  const onCellClicked = ({
    data,
    column,
  }: CellClickedEvent<AttendancePlayer>) => {
    // Read-only rows have nothing to open — the row press is an official's.
    if (
      !data ||
      isReadOnly ||
      isSpareCandidate ||
      !ROW_PRESS_COLUMN_IDS.includes(column.getColId())
    ) {
      return
    }
    if (isWaiverLocked(data, waiverEnforced, isSpareCandidate)) {
      message.info('Waiver is not signed by this player')
      return
    }
    onPressRow(data)
  }

  return (
    <div>
      <p css={labelStyle}>{`${capitalizeWords(label)} (${count})`}</p>
      {note ? <p css={noteStyle}>{note}</p> : null}
      <AttendanceSectionContext.Provider value={config}>
        <DataGrid<AttendancePlayer>
          css={gridStyle}
          rowData={rows}
          columnDefs={columnDefs}
          getRowId={({ data }) => data.id}
          defaultColDef={{
            // Row order is owned by orderAttendanceRows; the header cells
            // drive it (see AttendanceSortHeader).
            sortable: false,
            resizable: false,
            suppressMovable: true,
            cellClass: ATTENDANCE_CELL_CLASS,
          }}
          rowClassRules={{
            [LOCKED_ROW_CLASS]: ({ data }) =>
              !!data && isWaiverLocked(data, waiverEnforced, isSpareCandidate),
          }}
          onCellClicked={onCellClicked}
          domLayout='autoHeight'
          // Empty sections render nothing, so the grid never needs room for
          // its no-rows overlay — size it to its rows.
          autoHeightMinBodyHeight={0}
          // The columns outgrow the modal, and the modal body — not the grid —
          // scrolls vertically, so the scrollbar would otherwise only show once
          // the user reaches the section's last row.
          stickyHorizontalScrollbar
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          suppressCellFocus
          data-testid='attendance-grid'
        />
      </AttendanceSectionContext.Provider>
    </div>
  )
}

export default AttendanceSection

// Pulled up by the title's 8px bottom margin so the note sits directly under
// it, then keeps that 8px gap before the table below.
const noteStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 14px;
  margin: -8px 0 8px;
`
const labelStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 16px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  font-weight: 600;
  margin: 16px 0 8px;
`
const gridStyle = css`
  .${ATTENDANCE_CELL_CLASS} {
    display: flex;
    align-items: center;
  }
  .${ATTENDANCE_CENTER_CLASS} {
    justify-content: center;
  }
  .ag-header-cell.${ATTENDANCE_CENTER_CLASS} .ag-header-cell-label,
  .ag-header-cell.${ATTENDANCE_CENTER_CLASS} .ag-header-cell-comp-wrapper,
  .ag-header-cell.${ATTENDANCE_CENTER_CLASS} button {
    justify-content: center;
  }
  /* Unsigned waiver: controls are locked, the row steps back. */
  .${LOCKED_ROW_CLASS} {
    opacity: 0.5;
  }
`
